import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { reconcileActiveComp } from './reconcileActiveComp';
import { useQuery } from '@tanstack/react-query';
import { api, type Competition, type OrgMembershipSummary, type OrganizationSummary, type Me } from './api';
import { useEffectiveMe, useViewAs } from './viewAs';
import { filterCompsToScope, resolveCompScope } from './compScope';

// ─────────────────────────────────────────────────────────────────────────────
// Workspace — global active competition + active org context.
//
// Multi-tenant SaaS pattern: the "current workspace" is a single piece of
// global state that every page inherits. Replaces the per-page comp pickers
// that used to exist on Standings/Matches/Seeding/Divisions, and the
// previously local-only "active org" key in MyOrg.tsx.
//
// Stored in localStorage so the user's last selections persist across sessions.
//   active comp: ggm_active_comp (existing key, untouched semantics)
//   active org : ggm.workspace.activeOrgId (new key — fresh namespace; the
//                old MyOrg-local "ggm_active_org" key is migrated on read so
//                users don't lose their selection)
// ─────────────────────────────────────────────────────────────────────────────

const COMP_STORAGE_KEY = 'ggm_active_comp';
const ORG_STORAGE_KEY = 'ggm.workspace.activeOrgId';
const ORG_STORAGE_KEY_LEGACY = 'ggm_active_org';
const ENTRY_ORG_KEY = 'ggm_entry_org';

// T07: the org whose Discord the user entered through (written by Handshake.tsx
// from the redeem response). Deliberately separate from activeOrgId — entry
// scoping works for guests who aren't members of the org yet, while
// activeOrgId is reconciled against memberships.
export type EntryOrg = { orgId: string; orgName: string };

function readEntryOrg(): EntryOrg | null {
  try {
    const raw = sessionStorage.getItem(ENTRY_ORG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<EntryOrg>;
    if (typeof parsed.orgId !== 'string' || !parsed.orgId) return null;
    return { orgId: parsed.orgId, orgName: typeof parsed.orgName === 'string' ? parsed.orgName : parsed.orgId };
  } catch {
    return null;
  }
}

type WorkspaceValue = {
  activeComp: Competition | null;
  activeCompId: string | null;
  setActiveCompId: (id: string) => void;
  comps: Competition[];
  /** comps filtered to the effective scope — what pickers should show */
  visibleComps: Competition[];
  /** True when `visibleComps` is a narrowed slice rather than the whole list.
   *  THE single authority — consumers must read this, never re-derive it from
   *  `entryOrg`/`isFounder`. The leak this replaced was exactly that expression
   *  copy-pasted into a second file. */
  orgScoped: boolean;
  isLoading: boolean;

  // Active org context — added Phase 3.5.
  activeOrgId: string | null;
  setActiveOrgId: (id: string | null) => void;
  activeOrg: OrganizationSummary | null;
  memberships: OrgMembershipSummary[];
  isFounder: boolean;
  meLoading: boolean;

  // T07 entry scoping
  entryOrg: EntryOrg | null;
  clearEntryOrg: () => void;
};

const WorkspaceContext = createContext<WorkspaceValue | null>(null);

function readInitialActiveOrg(): string | null {
  try {
    const v = localStorage.getItem(ORG_STORAGE_KEY);
    if (v) return v;
    // Migrate legacy key once.
    const legacy = localStorage.getItem(ORG_STORAGE_KEY_LEGACY);
    if (legacy) {
      try { localStorage.setItem(ORG_STORAGE_KEY, legacy); } catch { /* ignore */ }
      return legacy;
    }
    return null;
  } catch {
    return null;
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { data: comps = [], isLoading } = useQuery({
    queryKey: ['competitions'],
    queryFn: api.competitions,
  });

  const { data: me, isLoading: meLoading } = useQuery<Me>({
    queryKey: ['me'],
    queryFn: api.me,
  });

  // Hydrate org names. The /me payload only carries memberships (orgId+role),
  // not names — fetch the user's orgs separately. This is exactly what the
  // pre-Phase-3.5 MyOrg.tsx did with its own useQuery; lifting it here means
  // every page gets the cached result.
  // useMemo, not a bare `?? []`: a fresh array literal on every render lands in
  // the dependency array of the reconcile effect below and makes it re-run on
  // every render (2026-07-22 workspace-flicker scar tissue).
  const memberships = useMemo(() => me?.orgMemberships ?? [], [me?.orgMemberships]);
  const { data: orgsList } = useQuery({
    queryKey: ['orgs'],
    queryFn: api.orgs,
    enabled: memberships.length > 0,
  });

  const [activeCompId, setActiveCompIdRaw] = useState<string | null>(() => {
    try { return localStorage.getItem(COMP_STORAGE_KEY); } catch { return null; }
  });

  const [activeOrgId, setActiveOrgIdRaw] = useState<string | null>(readInitialActiveOrg);
  const [entryOrg, setEntryOrg] = useState<EntryOrg | null>(readEntryOrg);

  // VISIBILITY reads the EFFECTIVE (view-as-adjusted) role; PERSISTENCE above
  // deliberately does not.
  //
  // This flag decides what the sidebar shows, and it used to read the RAW
  // `me`, so it stayed true for the entire length of a view-as preview. With
  // `orgScoped` defined as `entryOrg !== null && !isFounder`, that meant a
  // founder previewing "member" was still handed the unfiltered god list, and
  // CompSwitcher — which trusts these two values — rendered every customer
  // org's competition names and statuses inside what the founder believed was
  // a member's view. Same class of bug as the pre-effectiveMe admin gates:
  // a simulated identity is only as honest as the values derived from it.
  //
  // `memberships` above stays on the raw `me` ON PURPOSE. It feeds the
  // active-org reconcile effect, which WRITES localStorage — deriving it from
  // the simulation would let a preview overwrite the founder's real stored
  // workspace and outlive the preview. Preview changes what you SEE, never
  // what you HAVE. (Identity no-op when no view-as is active — see
  // lib/viewAs.ts's applyViewAs.)
  const effectiveMe = useEffectiveMe(me);
  const isFounder = effectiveMe?.role === 'founder';

  // The RAW preview session, not the simulated identity. Two different
  // questions: `isFounder` asks "who am I pretending to be", `viewAs` asks "am
  // I pretending at all". Only the second can keep a preview off the
  // platform-wide list, because the first is already the persona's answer.
  const { viewAs } = useViewAs();
  const previewActive = viewAs !== null;

  // ONE authority for "which comps may this session see" — lib/compScope.ts.
  // Do not re-derive this rule anywhere else; the leak that made it necessary
  // was an inline expression whose `entryOrg !== null` precondition silently
  // disabled scoping on the founder's normal login. Under a preview the
  // resolver CANNOT return `all`, so there is no platform-wide list to fall
  // back to and no fallback to accidentally re-add.
  const compScope = useMemo(() => resolveCompScope({
    previewActive,
    previewOrgId: viewAs?.org?.orgId ?? null,
    entryOrgId: entryOrg?.orgId ?? null,
    isFounder,
  }), [previewActive, viewAs?.org?.orgId, entryOrg?.orgId, isFounder]);

  const orgScoped = compScope.kind !== 'all';
  // Memoized for the same reason as `memberships` above — `.filter()` returns a
  // new array every render, and this value is a dependency of the reconcile
  // effect below.
  const visibleComps = useMemo(() => filterCompsToScope(comps, compScope), [comps, compScope]);

  const clearEntryOrg = () => {
    setEntryOrg(null);
    try { sessionStorage.removeItem(ENTRY_ORG_KEY); } catch { /* ignore */ }
  };

  // Handshake.tsx writes the entry org AFTER this provider mounts (the
  // provider wraps the handshake page too) — re-read on its signal. Same
  // window-event pattern viewAs uses.
  useEffect(() => {
    const onChange = () => setEntryOrg(readEntryOrg());
    window.addEventListener('ggm-entry-org-changed', onChange);
    return () => window.removeEventListener('ggm-entry-org-changed', onChange);
  }, []);

  // ONE reconcile effect, one authority (see lib/reconcileActiveComp.ts).
  //
  // This used to be TWO effects — "seed if empty" and "reset if not visible" —
  // and the second one fought MyCompDetail.tsx, which pins the comp page you
  // are looking at. Opening a PENDING comp (absent from visibleComps) made them
  // argue forever and the switcher label flickered between the comp name and
  // "Pick a comp" several times a second. The reconciler now corrects only a
  // comp that exists in NO list; one that is merely filtered out of the visible
  // slice is the user's deliberate choice and is left alone.
  //
  // SUSPENDED DURING A PREVIEW, and that is a security property rather than a
  // nicety. This effect is a WRITER — it persists to localStorage — and
  // reconcileActiveComp seeds from `visibleComps.length > 0 ? visibleComps :
  // comps`, so an empty preview slice sent it to the founder's full
  // platform-wide list, picked another org's comp, and wrote it to storage
  // where it outlived the preview. Same rule as `memberships` above: preview
  // changes what you SEE, never what you HAVE. Leaving the founder's real
  // selection untouched for the duration is exactly right.
  useEffect(() => {
    if (previewActive) return;
    const next = reconcileActiveComp({ activeCompId, comps, visibleComps, isLoading });
    if (next === null || next === activeCompId) return;
    setActiveCompIdRaw(next);
    try { localStorage.setItem(COMP_STORAGE_KEY, next); } catch { /* ignore */ }
  }, [comps, visibleComps, activeCompId, isLoading, previewActive]);

  // Reconcile active org with available memberships once data lands. If the
  // stored org isn't (or is no longer) one the user belongs to, fall through
  // to memberships[0]. If memberships is empty, leave activeOrgId at null.
  useEffect(() => {
    if (memberships.length === 0) {
      // Clear stale stored value if the user has no memberships.
      if (activeOrgId !== null) {
        setActiveOrgIdRaw(null);
        try { localStorage.removeItem(ORG_STORAGE_KEY); } catch { /* ignore */ }
      }
      return;
    }
    if (!activeOrgId || !memberships.find(m => m.orgId === activeOrgId)) {
      const next = memberships[0].orgId;
      setActiveOrgIdRaw(next);
      try { localStorage.setItem(ORG_STORAGE_KEY, next); } catch { /* ignore */ }
    }
  }, [memberships, activeOrgId]);

  // useCallback is LOAD-BEARING, not tidiness. MyCompDetail.tsx lists
  // setActiveCompId in a useEffect dependency array; when this was a plain
  // function it got a new identity on every render, so that effect re-fired on
  // every render and re-pinned the comp — the detonator for the 2026-07-22
  // workspace flicker. Do not unwrap these.
  const setActiveCompId = useCallback((id: string) => {
    setActiveCompIdRaw(id);
    try { localStorage.setItem(COMP_STORAGE_KEY, id); } catch { /* ignore */ }
  }, []);

  const setActiveOrgId = useCallback((id: string | null) => {
    setActiveOrgIdRaw(id);
    try {
      if (id) localStorage.setItem(ORG_STORAGE_KEY, id);
      else localStorage.removeItem(ORG_STORAGE_KEY);
    } catch { /* ignore */ }
  }, []);

  // Resolved from the FULL list for a real session — deliberate, and load-
  // bearing: MyCompDetail pins the comp page you are on, including PENDING
  // comps that are filtered out of the visible slice, and CompSwitcher's label
  // must still name it (2026-07-22 flicker scar tissue).
  //
  // Under a PREVIEW it resolves from the visible slice instead. CompSwitcher
  // renders `activeComp.name` as its headline, so a founder whose real
  // selection was another org's comp kept that name on screen for the whole
  // preview — the dropdown was correctly filtered while the header above it
  // still leaked. Out-of-scope means "Pick a comp", which is what the persona
  // would genuinely see.
  const activeCompPool = previewActive ? visibleComps : comps;
  const activeComp = activeCompPool.find(c => c.id === activeCompId) || null;
  const activeOrg = (orgsList ?? []).find(o => o.id === activeOrgId) ?? null;

  const value: WorkspaceValue = {
    activeComp,
    activeCompId,
    setActiveCompId,
    comps,
    visibleComps,
    orgScoped,
    isLoading,
    activeOrgId,
    setActiveOrgId,
    activeOrg,
    memberships,
    isFounder,
    meLoading,
    entryOrg,
    clearEntryOrg,
  };

  return createElement(WorkspaceContext.Provider, { value }, children);
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}
